import React from "react";
import * as Sentry from "@sentry/react";
import {BrowserTracing, EventHint} from "@sentry/react";
import history from "history/browser";
import {store} from "./redux/store";
import {ApolloProvider} from "@apollo/client";
import {apolloClient} from "./api/clients";
import {createRoot} from "react-dom/client";
import {Provider} from "react-redux";
import App from "./app";
import {Theme} from "./utils/theme";
import {RefsContextProvider} from "./utils/refContext";
import "bootstrap/js/dist/dropdown.js"
import 'react-loading-skeleton/dist/skeleton.css'
import {isDevelopmentBuild, isPreProd} from "./utils/constants";
import {AppContext} from "./definitions/types";
import {identity, isApolloUnauthorizedError} from "./utils/helpers";
import "../wdyr"
import {AuthToken} from "./utils/authToken";

const {claims} = identity();
const viewModel = window.viewModel as AppContext;

// Seed the initial product into the current history state
history.replace(window.location.href, {productId: viewModel.productId, primaryId: viewModel.primaryId});

Sentry.init({
    dsn: "https://172bea4bdb8dd820a7b46b8f2cbbb531@o4504793116246016.ingest.sentry.io/4505794954199040",
    environment: isDevelopmentBuild ? 'dev' : isPreProd ? 'preprod' : 'prod',
    integrations: [new Sentry.Replay(), new BrowserTracing()],
    tracesSampleRate: isDevelopmentBuild ? 0 : 0.01,
    replaysOnErrorSampleRate: 0, // isDevelopmentBuild ? 0 : .25,
    replaysSessionSampleRate: 0,
    debug: isDevelopmentBuild,
    enabled: !isDevelopmentBuild,
    beforeSend(event, {originalException, syntheticException}: EventHint) {
        // Filter out unauthorized errors thrown by Apollo Client to avoid spamming Sentry
        if (isApolloUnauthorizedError(originalException)) return null;

        // Append the original exception to the event's extra data, if any
        if (originalException)
            event.extra = {...event.extra, originalException};

        // Append the synthetic exception to the event's extra data, if any
        if (syntheticException)
            event.extra = {...event.extra, syntheticException};

        return event;
    },
    ignoreErrors: [
        'TypeError: Failed to fetch',
        'TypeError: NetworkError when attempting to fetch resource.'
    ],
    initialScope: {
        tags: {
            productId: viewModel.productId,
            primaryId: viewModel.primaryId,
            catalogId: viewModel.catalogId,
            currencyCode: viewModel.currencyCode,
            sku: viewModel.sku
        },
        user: claims && {
            sub: claims.sub,
            aud: claims.aud,
            email: claims.email,
            oid: claims.oid,
            uca: claims.uca
        }
    }
});

const container = document.getElementById("products-root");
const root = createRoot(container!);

root.render(
    <ApolloProvider client={apolloClient}>
        <RefsContextProvider>
            <Provider store={store}>
                <AuthToken>
                    <Theme>
                        <App/>
                    </Theme>
                </AuthToken>
            </Provider>
        </RefsContextProvider>
    </ApolloProvider>
);